import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { attachBrowserListeners, trackNavigation } from './browser';
import { successKindFromState } from './routePatterns';

/**
 * Renders nothing. Reports each page a signed-in user opens, and how long it stays
 * visible. Mount once, inside the router. Reads the pathname only (plus, for the shared
 * /success page, one fixed outcome word from the navigation state): the query string, the
 * hash and every other part of the state are never looked at.
 */
export const RouteTracker = (): null => {
  const { pathname, state } = useLocation();
  const kind = successKindFromState(state);
  const pageRef = useRef({ pathname, kind });
  pageRef.current = { pathname, kind };

  useEffect(() => {
    try {
      trackNavigation(pathname, kind);
    } catch {
      // Tracking must never break the app.
    }
  }, [pathname, kind]);

  useEffect(() => {
    // A sign-in on a public page does not change the path; pick the page up when it happens.
    const onAuthChange = (): void => {
      try {
        trackNavigation(pageRef.current.pathname, pageRef.current.kind);
      } catch {
        // Tracking must never break the app.
      }
    };
    window.addEventListener('authChange', onAuthChange);
    const detach = attachBrowserListeners();
    return () => {
      window.removeEventListener('authChange', onAuthChange);
      detach();
    };
  }, []);

  return null;
};

export default RouteTracker;
